import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { controller } from '../../api/controller';
import { usePublicUiLocale } from '../../contexts/PublicUiLocaleContext';
import HelpHint from '../../components/HelpHint';
import {
  parseProjectNameTakenError,
  projectNameTakenMessage,
} from '../../project-modules/projectNameHelpers';

function parseApiMessage(err, fallback) {
  const msg = err?.response?.data?.message;
  if (typeof msg === 'string') return msg;
  if (typeof msg === 'object' && msg !== null && typeof msg.message === 'string') {
    return msg.message;
  }
  return fallback;
}

export default function RenameProjectDialog({
  open,
  project,
  onClose,
  onRenamed,
}) {
  const { sa } = usePublicUiLocale();
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [nameTaken, setNameTaken] = useState(false);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [checkingName, setCheckingName] = useState(false);

  useEffect(() => {
    if (open && project) {
      setName(project.name || '');
      setError('');
      setNameTaken(false);
      setNameSuggestions([]);
    }
  }, [open, project]);

  useEffect(() => {
    if (!open || !project?.id) return undefined;
    const trimmed = name.trim();
    if (!trimmed || trimmed === String(project.name || '').trim()) {
      setNameTaken(false);
      setNameSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setCheckingName(true);
      try {
        const res = await controller.get(
          `/catalog/projects/suggest-name?name=${encodeURIComponent(trimmed)}&excludeId=${project.id}`,
        );
        if (cancelled) return;
        const taken = !!res.data?.taken;
        setNameTaken(taken);
        setNameSuggestions(taken ? (res.data?.suggestions || []) : []);
      } catch {
        if (!cancelled) {
          setNameTaken(false);
          setNameSuggestions([]);
        }
      } finally {
        if (!cancelled) setCheckingName(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, project, name]);

  const handleSubmit = async () => {
    if (!project?.id) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError(sa.uidRenameRequired);
      return;
    }
    if (trimmed === String(project.name || '').trim()) {
      onClose();
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await controller.renameProject(project.id, { name: trimmed });
      onRenamed?.();
      onClose();
    } catch (e) {
      const suggestions = parseProjectNameTakenError(e);
      if (suggestions.length) {
        setNameSuggestions(suggestions);
        setNameTaken(true);
      }
      setError(projectNameTakenMessage(e, parseApiMessage(e, sa.createFailed)));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {sa.renameProjectTitle}
        <HelpHint title={sa.helpRenameProjectTitle} text={sa.helpRenameProject} />
      </DialogTitle>
      <DialogContent>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          {sa.renameProjectSlugHint}
        </Typography>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label={sa.name}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={nameTaken}
          helperText={nameTaken ? sa.projectNameTaken : undefined}
        />
        {nameTaken && nameSuggestions.length > 0 ? (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
              {sa.projectNameSuggestions}
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {nameSuggestions.map((suggestion) => (
                <Chip
                  key={suggestion}
                  size="small"
                  label={suggestion}
                  clickable
                  variant="outlined"
                  onClick={() => setName(suggestion)}
                />
              ))}
            </Stack>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>{sa.cancel}</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || nameTaken || checkingName}
        >
          {submitting ? sa.creating : sa.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

RenameProjectDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  project: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    slug: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onRenamed: PropTypes.func,
};
